<script setup lang="ts">
import { computed, reactive, watch } from 'vue';
import ElementLibrarySelector from '@/components/DesignStudio/ElementLibrarySelector.vue';
import MaskSelectorModal from '@/components/DesignStudio/MaskSelectorModal.vue';
import type { BoundedTextElement, DynamicImage, TextShapeConfig } from '@/typings/customilyElements/canvasElements';
import type { CanvasEx } from '@/typings/canvas';
import type { KeyValueDto } from '@/typings/keyValueDto';
import type { MaskDto } from '@/api/typings/mask';
import type { ElementMapOption } from './types';
import { LibraryTypeEnum } from '@/utils/enums/libraryType.enum';
import { MaskType } from '@/utils/enums/maskType.enum';
import { applyTextShapeOption, normalizeTextShapeConfig, placeShapePaths } from '@/utils/textShapeUtils';
import { useToaster } from '@/composables/toaster';

const IMAGE_TYPE = LibraryTypeEnum.Image;
const TEXT_SHAPE_TYPE = MaskType.TextShape;
// A shape can come from an image library (png/jpg) or a vector one (its elements resolve to svg)
const SHAPE_LIBRARY_TYPES = [LibraryTypeEnum.Image, LibraryTypeEnum.Vector];

const props = defineProps<{
    canvas: CanvasEx;
    textElement?: BoundedTextElement;
    editMode?: boolean;
}>();

const emit = defineEmits<{
    (e: 'open-shape-library-selector', libraryKind: 'image' | 'vector'): void;
    (e: 'trigger-loading', isLoading: boolean, msg?: string): void;
    (e: 'element-props-updated', value: string): void;
}>();

const toaster = useToaster();

const model = reactive({
    shapeOption: 1,
    shapeMap: new Map<number, DynamicImage>(),
    shapeLibraryId: undefined as number | undefined,
    picker: {
        open: false,
        positionToEdit: undefined as number | undefined
    }
});

const disabled = computed(() => !!props.textElement?.locked);

const initShapeMap = (images: [number, string][]) => {
    const newMap = new Map<number, DynamicImage>();
    for (const [position, image] of images) {
        if (!image) continue;

        newMap.set(position, { path: image, fileName: '' });
    }
    model.shapeMap = newMap;
};

watch(
    () => props.textElement,
    (element) => {
        if (!element) return;

        model.shapeOption = element.shapeOption || 1;
        model.shapeLibraryId = element.shapeConfig?.libraryId;
        initShapeMap(element.shapeConfig?.shapeMap || []);
    },
    { immediate: true }
);

const displayLoader = (active = true, message?: string) => emit('trigger-loading', active, message);

const notifyChange = (element: BoundedTextElement) => {
    props.canvas?.requestRenderAll();
    element.fire('modified');
    emit('element-props-updated', element.uuid as string);
};

const updateOptionSelected = async (option: number, value?: ElementMapOption) => {
    const element = props.textElement;
    if (!element) return;

    const path = typeof value == 'string' ? value : (value as DynamicImage | undefined)?.path;
    const previousShape = element.textShape;
    model.shapeOption = option;
    try {
        displayLoader(true, 'Loading shape...');
        await applyTextShapeOption(element, option, path);
    } catch (error) {
        console.error(error);
        toaster.error('The shape image could not be applied, make sure it has a visible shape');
    } finally {
        displayLoader(false);
    }
    if (element.textShape !== previousShape) notifyChange(element);
};

const updateShapeConfig = (config: Partial<TextShapeConfig>) => {
    const element = props.textElement;
    if (!element) return;

    const newConfig = normalizeTextShapeConfig({
        shapeMap: [],
        ...element.shapeConfig,
        ...config
    });
    initShapeMap(newConfig?.shapeMap || []);
    model.shapeLibraryId = newConfig?.libraryId;

    element.set('shapeConfig', newConfig);
    // A shape forces multiline and alignment, so those change along with it
    if (props.editMode) element.setEditedProp('shapeConfig', 'multiline', 'textAlign', 'textVerticalAlign');
};

const openPicker = (position?: number) => {
    model.picker.open = true;
    model.picker.positionToEdit = position;
};

const closePicker = () => {
    model.picker.open = false;
    model.picker.positionToEdit = undefined;
};

// Editing a position replaces it, adding appends after the last one
const onShapesPicked = async (shapes: MaskDto[]) => {
    const element = props.textElement;
    // By the highest position, not by the count: a stored config can have gaps (normalizeTextShapeConfig
    //  drops the positions without a path), and there appending by count would overwrite a shape
    const lastPosition = Math.max(0, ...model.shapeMap.keys());
    const startPosition = model.picker.positionToEdit || lastPosition + 1;
    closePicker();
    if (!element || !shapes.length) return;

    const shapeMap = placeShapePaths(
        element.shapeConfig?.shapeMap || [],
        shapes.map(({ path }) => path),
        startPosition
    );
    updateShapeConfig({ shapeMap });
    await updateOptionSelected(startPosition + shapes.length - 1);
};

const removeImageOptionFromMap = (position: number) => {
    const shapes = new Map(props.textElement?.shapeConfig?.shapeMap);
    shapes.delete(position);
    // Keep positions consecutive, options refer to them by index
    updateShapeConfig({ shapeMap: [...shapes.values()].map((path, index) => [index + 1, path]) });
    updateOptionSelected(1);
};

const onImageOptionsReordered = (options: KeyValueDto<number, ElementMapOption>[]) => {
    const shapeMap = options.map<[number, string]>(({ value }, index) => [index + 1, (value as DynamicImage).path]);
    updateShapeConfig({ shapeMap });
    updateOptionSelected(1);
};

// The controller opens the matching picker, it can't read the (const) enum from an angular file
const onPickLibrary = (libraryType?: LibraryTypeEnum) =>
    emit('open-shape-library-selector', libraryType === LibraryTypeEnum.Vector ? 'vector' : 'image');

const removeImageLibrary = () => {
    updateShapeConfig({ libraryId: undefined });
    updateOptionSelected(1);
};
</script>

<template>
    <div class="text-shape-wrapper">
        <element-library-selector
            :disabled="disabled"
            :element-list="model.shapeMap"
            :library-id="model.shapeLibraryId"
            :library-type="IMAGE_TYPE"
            :selected-position="model.shapeOption"
            :element-list-position="3"
            :library-pick-types="SHAPE_LIBRARY_TYPES"
            option-wording="shape"
            external-image-picker
            @open-image-picker="openPicker"
            @load-option="updateOptionSelected"
            @options-reordered="onImageOptionsReordered"
            @remove-map-option="removeImageOptionFromMap"
            @remove-library="removeImageLibrary"
            @pick-library="onPickLibrary"
        />

        <mask-selector-modal
            v-if="model.picker.open"
            :type="TEXT_SHAPE_TYPE"
            :multiple="!model.picker.positionToEdit"
            @select="onShapesPicked([$event])"
            @select-multiple="onShapesPicked"
            @cancel="closePicker"
            @error="toaster.error($event)"
        />
    </div>
</template>
